import * as React from 'react';

import { StatusChip } from '@/design';
import { useCan } from '@/domains/auth';
import type {
  FirmFacetField,
  FirmRecordType,
  IAdminFirmRow,
  IAdminParticipantSecAdvFirm,
  IFirmsQuery,
  RecordListSecAdv
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { FirmLink } from '../../components/firm-link';
import { ROSTER_STATES, SEC_ADV_STATES } from '../../components/record-filters';
import { matchedByText } from '../../components/sec-adv-values';
import {
  accountColumn,
  createdColumn,
  latestResponseColumn,
  recordNameColumn,
  recordQueryOf,
  RosterSlots
} from './record-list';
import { RecordWorkArea, RecordWorkPage, type IRecordWorkSpec } from './record-work-page';

/**
 * Firms (SR-414, record search §3.3; the plan's D15) — one row per RIA firm or other organisation
 * we hold a record of, `GET /admin/firms` under `responses.browse`; and its record page beside the
 * list at its own address, `/team/firms/:participantId` (the owner's Q2), a firm and an org alike.
 * The record page is People's and moves itself here once its hub says it is one (D6).
 *
 * A broker-dealer is not a record here (record search D2): it is a firm name on a profile and in
 * Team firms, and the SEC's register is the SEC ADV register — the help says so.
 *
 * **The SEC ADV column is survey-api's match, drawn as sent** — each register firm one of the
 * firm's numbers names, by its legal name and the number types that named it. A match is not a
 * verification (the hub's rule). The register firm's card is `registry.browse`, so the name is a
 * link for an account holding it and text for any other: the card's guard, not a hidden column.
 *
 * The page is a light work page (SR-514, `record-work-page.tsx`): no cards and no counts read; the
 * record type, the SEC ADV match, the roster and the cycle are its columns' funnels.
 */
export function AdminFirmsArea() {
  return <RecordWorkArea spec={FIRMS} />;
}

export function AdminFirmsPage() {
  return <RecordWorkPage spec={FIRMS} />;
}

/** The Firms query, from the URL — the shared filters, `secAdv` and the record type, as
 *  spelled. */
function firmsQueryOf(params: URLSearchParams, page: number, pageSize: number): IFirmsQuery {
  return {
    ...recordQueryOf(params, page),
    pageSize,
    secAdv: (params.get('secAdv') ?? undefined) as RecordListSecAdv | undefined,
    recordType: (params.get('recordType') ?? undefined) as FirmRecordType | undefined
  };
}

const RECORD_TYPES = [
  { value: 'firm', label: 'Firm' },
  { value: 'org', label: 'Organization' }
];

/** The register firms the firm's numbers name — "Not matched" for none. */
function SecAdvMatches({ firms }: { firms: readonly IAdminParticipantSecAdvFirm[] }) {
  const canBrowse = useCan()('registry.browse');
  if (firms.length === 0) return <span className="text-muted-foreground">Not matched</span>;
  return (
    <ul aria-label="SEC ADV register" className="grid gap-1">
      {firms.map((firm) => (
        <li key={firm.firmCrd}>
          {canBrowse ? (
            <FirmLink firm={firm} className="break-words" />
          ) : (
            <span className="break-words">{firm.legalName}</span>
          )}
          <p className="text-xs text-muted-foreground">by {matchedByText(firm.matchedBy)}</p>
        </li>
      ))}
    </ul>
  );
}

const FIRMS: IRecordWorkSpec<IAdminFirmRow, IFirmsQuery> = {
  area: 'firms',
  counted: ['firm', 'firms'],
  placeholder: 'Firm, principal, address or number',
  about: (
    <>
      The RIA firms and the other organisations we hold a record of. A broker-dealer is a firm name
      on a profile and in Team firms; the SEC’s register is the SEC ADV register. An Organization is
      a record that has not answered the RIA firm survey; a firm is one with an RIA firm response.
    </>
  ),
  filters: ['secAdv', 'recordType'],
  queryOf: firmsQueryOf,
  list: (query, signal) => participantsApi.listFirms(query, signal),
  facets: (field, query, prefix, signal) =>
    participantsApi.firmFacets(field as FirmFacetField, query, prefix, signal),
  sorts: {
    name: { key: 'name', first: 'asc' },
    'latest-response': { key: 'last_change', first: 'desc' },
    created: { key: 'created', first: 'desc' }
  },
  funnels: [
    { column: 'name', groups: [{ param: 'recordType', label: 'Record type', known: RECORD_TYPES }] },
    { column: 'sec-adv', groups: [{ param: 'secAdv', label: 'SEC ADV', known: SEC_ADV_STATES }] },
    { column: 'principals', groups: [{ param: 'roster', label: 'Roster', known: ROSTER_STATES }] },
    { column: 'latest-response', groups: [{ param: 'cycle', label: 'Cycle', known: 'cycles' }] }
  ],
  columns: (search, q) => [
    recordNameColumn<IAdminFirmRow>('firms', 'Firm', search, q, (firm) =>
      firm.recordType === 'org' ? <StatusChip>Organization</StatusChip> : null
    ),
    {
      id: 'sec-adv',
      header: 'SEC ADV',
      cell: (firm) => <SecAdvMatches firms={firm.secAdvFirms} />
    },
    {
      id: 'principals',
      header: 'Principals',
      cell: (firm) => <RosterSlots label="Principals" members={firm.principals} />
    },
    accountColumn(),
    latestResponseColumn(),
    createdColumn()
  ],
  secondary: (firm) => {
    const sec = firm.identifiers.find((identifier) => identifier.idType === 'SEC_NUMBER');
    return sec ? `SEC ${sec.value}` : 'No SEC number';
  }
};
